/**
 * The band policy the two ranged brains share (PRD #793).
 *
 * A ranged monster wants to be neither in your face nor out of the fight, so it
 * declares a **band**: a preferred distance with a near edge it backs off from
 * and a far edge it closes from. Three rules turn that into behavior a player
 * can beat, and they are here rather than duplicated because a kiter and a
 * caster differ in what they do *inside* the band, not in how they hold it.
 *
 * **Hysteresis.** The band is a range, not a point, so a monster inside it does
 * not move at all. A single preferred distance would have every monster
 * permanently correcting by inches.
 *
 * **Bounded reposition.** Backing off moves {@link RepositionBand.repositionTiles}
 * and no further, so a pressured monster gives ground once rather than running
 * the length of the room. Combined with the near edge, closing the gap is
 * rewarded: a player who keeps walking in stays inside the pressure distance and
 * the monster runs out of room to give.
 *
 * **Anti-oscillation.** A reposition is committed for a fixed number of ticks
 * ({@link BrainMemory.commitTicks}), during which the brain keeps walking to the
 * point it chose instead of re-deciding. Without it a monster sitting exactly on
 * a band edge alternates retreat and approach every tick and visibly twitches.
 *
 * **Rng draws: one per committed reposition**, a lateral scatter angle, taken at
 * the moment the reposition is committed and never again while it runs. It is
 * what stops a pack of archers from retreating along one line into one corner.
 */

import type { BrainMemory, BrainPoint, BrainRng, PerceivedTarget } from './types';
import { directionBetween } from './perception';

export interface RepositionBand {
  /** Inside this, the monster backs off. */
  pressureTiles: number;
  /** Outside this, the monster closes. Between the two it holds. */
  farTiles: number;
  /** How far one committed reposition moves. */
  repositionTiles: number;
  /** How long a committed reposition runs before the brain re-decides. */
  commitTicks: number;
}

/** The widest a reposition may deviate from straight away, in radians. */
const SCATTER_RADIANS = Math.PI / 6;

export type BandDecision =
  | { kind: 'hold' }
  | { kind: 'approach'; point: BrainPoint }
  | { kind: 'retreat'; point: BrainPoint; memory: BrainMemory };

/**
 * Where the band says to go, given a perceived target.
 *
 * Returns `hold` inside the band, an approach point outside its far edge, and a
 * committed retreat point inside its pressure distance. The caller decides what
 * to do while holding: firing, casting, or waiting on a cooldown.
 */
export function holdBand(
  self: BrainPoint,
  target: PerceivedTarget,
  band: RepositionBand,
  memory: BrainMemory,
  rng: BrainRng,
): BandDecision {
  if (target.distanceTiles < band.pressureTiles) {
    const away = directionBetween(target.position, self) ?? { x: 0, y: 1 };
    // One draw, here and nowhere else in a band decision.
    const scatter = (rng.next() * 2 - 1) * SCATTER_RADIANS;
    const cos = Math.cos(scatter);
    const sin = Math.sin(scatter);
    const point = {
      x: self.x + (away.x * cos - away.y * sin) * band.repositionTiles,
      y: self.y + (away.x * sin + away.y * cos) * band.repositionTiles,
    };
    return {
      kind: 'retreat',
      point,
      memory: { ...memory, commitTicks: band.commitTicks, commitPoint: point },
    };
  }
  if (target.distanceTiles > band.farTiles) {
    return { kind: 'approach', point: { ...target.position } };
  }
  return { kind: 'hold' };
}

/**
 * The reposition already in flight, or null when none is.
 *
 * Counting down here rather than in each brain keeps "a commitment is a number
 * of ticks" in one place; a brain only has to ask whether it is still running.
 */
export function continueCommitment(
  memory: BrainMemory,
): { point: BrainPoint; memory: BrainMemory } | null {
  if (memory.commitTicks <= 0 || !memory.commitPoint) return null;
  return {
    point: { ...memory.commitPoint },
    memory: { ...memory, commitTicks: memory.commitTicks - 1 },
  };
}

/** Clear a commitment that a new decision has overtaken. */
export function clearCommitment(memory: BrainMemory): BrainMemory {
  return memory.commitTicks === 0 && memory.commitPoint === null
    ? memory
    : { ...memory, commitTicks: 0, commitPoint: null };
}
